import { Injectable } from '@angular/core';
import { UserManager, User, UserManagerSettings } from 'oidc-client';
import { BehaviorSubject } from 'rxjs';
import { BaseService } from "@shared/base.service";
import { environment } from '@environments';
import { TipoUsuario } from '@shared/enums/tipoUsuario';
import { Usuario } from '@shared/models/despacho/Usuario';
import { UsuarioService } from '@account/usuario.service';
import { AccionLogin } from '@shared/enums/AccionLogin';
import { MessageService } from '@shell/message.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {

  private _authNavStatusSource = new BehaviorSubject<boolean>(false);
  private _authAccionLoginSource = new BehaviorSubject<AccionLogin>(null);
  private _authProcesandoLoginSource = new BehaviorSubject<boolean>(false);
  private _usuarioSource = new BehaviorSubject<Usuario>(null);

  // Observable navItem stream
  authNavStatus$ = this._authNavStatusSource.asObservable();
  authAccionLogin$ = this._authAccionLoginSource.asObservable();
  //usuario$ = this._usuarioSource.asObservable();
  procesandoLogin$ = this._authProcesandoLoginSource.asObservable();

  currentUser: User | null;
  userManager = new UserManager(getClientSettings());

  private _usernameHeaderValue: string;

  constructor(
    private usuarioService: UsuarioService, 
    private messageService: MessageService) {
      super();

      /*
        Este evento se dispara cuando se hace el silentRenew
      */
  
      this.userManager.getUser()
        .then((user) => {
          if (user) {
            this._authProcesandoLoginSource.next(true);

            this.currentUser = user;

            this.handleUser(user);
          }
        })
        .catch((err) => {
          this.messageService.showErrorMessage(err)
        });
  
      this.userManager.events.addUserLoaded((user) => {
        this.currentUser = user;
      });
  }

  login(): Promise<void> {
    return this.userManager.signinRedirect();
  }

  getUser(): Promise<User | null> {
    return this.userManager.getUser();
  }

  handleUser = (user) => {
    if ( ! user.scope.includes("jwt") && user.access_token) {
      // Se obtienen datos por el profile o el accesstoken.
      // Si es login aoniken se decodifica el token y ya tenemos el usuario completo
      // Si es login YPF se decodifica parte para obtener profile encriptado.
      //    Se llama a usuarioActual de backend para que backend desencripte el profile y devuelva el usuario completo
      //    con sus roles.
      this.processLoginPorLibraryProfileAoniken();
    }else{
      this.processLoginPorAccessTokenYpf();
    }
  }

  convertirStringRoles(sRoles: string){
    //IDM-PAD-TEST-JEFEDEAEROPLANTA
    let arrayRoles = [];

    arrayRoles = sRoles.split(',')
      .map(r => {
        r = r.trim();
        r = r.replace("-", "_").replace("IDM_PAD_", "");
        const rolRecortado = r.toUpperCase();

        return TipoUsuario[rolRecortado];
      });

    return arrayRoles;
  }

  completarDatosUsuarioPorAccessTokenYpf(accessToken: string){
    const usuario = new Usuario();

    const tokenInfo = JSON.parse(atob(accessToken.split('.')[1]));
    const profileInfo = tokenInfo.token_details.Profile;

    usuario.nombresCompletos = profileInfo.last_name + ", " +profileInfo.first_name;
    usuario.email = profileInfo.mail;
    usuario.userName = tokenInfo.token_details.preferred_username;

    return usuario;
  }

  completarDatosUsuarioPorLibraryProfileAoniken(accessToken){
    const usuario = new Usuario();

    const tokenInfo = JSON.parse(atob(accessToken.split('.')[1]));

    usuario.nombresCompletos = tokenInfo.name;
    usuario.email = tokenInfo.email;
    usuario.userName = tokenInfo.unique_name;
    usuario.roles = this.convertirStringRoles(tokenInfo.roles.toString());
    usuario.sRoles = tokenInfo.roles.toString();

    return usuario;
  }

  completeAuthentication(): Promise<void> {
    return this.userManager.signinRedirectCallback().then(user => {
      this._authProcesandoLoginSource.next(this.isAuthenticated())
      this.handleUser(user);      
    });
  }

  processLoginPorLibraryProfileAoniken(){
    const accessToken = this.currentUser.access_token;

    const usuario = this.completarDatosUsuarioPorLibraryProfileAoniken(accessToken);

    this.loginUsuarioPad(usuario);
  }

  processLoginPorAccessTokenYpf(){
    const accessToken = this.currentUser.access_token;

    const usuario = this.completarDatosUsuarioPorAccessTokenYpf(accessToken);

    this.loginUsuarioPad(usuario);
  }

  loginUsuarioPad(usuario){

    this.usuarioService.loginUsuario(usuario).subscribe(
      usuarioAccionDto => {
        usuario = usuarioAccionDto.usuarioDTO;
        usuario.roles = this.usuarioService.getSeeHow(usuario.roles);
        usuario.sRoles = this.usuarioService.convertirRolesIntEnRolesString(usuario.roles);

        this._usuarioSource.next(usuario);
        this._usernameHeaderValue = usuario.userName;
        this._authAccionLoginSource.next(usuarioAccionDto.accion);
        this._authNavStatusSource.next(this.isAuthenticated());
        this._authProcesandoLoginSource.next(false);
      },
      (err) => {
        this.messageService.showErrorMessage(err.message + "\n" + err.description);
        this._authProcesandoLoginSource.next(false);
        this._authNavStatusSource.next(this.isAuthenticated());
      });
  }

  get userRoles(): string[] {

    const storedRole = sessionStorage.getItem('seeHow');
  
    // Si hay un rol en el sessionStorage, retorna un arreglo con ese único rol
    if (storedRole) {
      return [storedRole];
    }

    if (this.currentUser && this.currentUser.profile && this.currentUser.profile.roles) {
      this.currentUser.profile.roles = this.currentUser.profile.roles.map(r => {
        r = r.trim();
        r = r.replace("-", "_").replace("IDM_PAD_", "");
        return r.toUpperCase();
      });
        
      return Array.isArray(this.currentUser.profile.roles) ? this.currentUser.profile.roles : this.recortarPrefijoRol(this.currentUser.profile.roles);
    }else{
      // Autenticacion ypf
      return this.usuarioService.getArrayStringRoles();
    }
  }

  /**
   * Recorta "IDM-PAD-TEST-" de los roles
   * @param roles El string de roles como viene desde el servidor de autenticación
   */
  recortarPrefijoRol(roles: string): string[] {
    return roles.split(', ')
      .map(r => {
        r = r.substring(r.lastIndexOf('-') + 1).toLowerCase();
        return r.charAt(0).toUpperCase() + r.slice(1);
      });
  }

  hasRole(...roles: TipoUsuario[]): boolean {
    const hasRole = this.userRoles.some(r => roles.indexOf(TipoUsuario[r]) !== -1);

    return hasRole;
  }

  isAuthenticated(): boolean {
    return this.currentUser != null && !this.currentUser.expired;
  }

  get authorizationHeaderValue(): string {
    return `${this.currentUser.token_type} ${this.currentUser.access_token}`;
  }

  get usernameHeaderValue(): string {
    if( ! this._usernameHeaderValue ) return "";
    return this._usernameHeaderValue;
  }

  signout() {
    this.userManager.signoutRedirect();
  }
}

export function getClientSettings(): UserManagerSettings {
  return {
    client_id: environment.authentication.client_id,
    authority: environment.authentication.authority,
    redirect_uri: environment.authentication.redirect_uri,
    post_logout_redirect_uri: environment.authentication.post_logout_redirect_uri,
    response_type: environment.authentication.response_type,
    loadUserInfo: environment.authentication.loadUserInfo,
    scope: environment.authentication.scope,
    automaticSilentRenew: environment.authentication.automaticSilentRenew,
    silent_redirect_uri: environment.authentication.silent_redirect_uri
  };
}

